/* eslint-disable indent */
import React, { MouseEvent, useState } from "react";
import ReactPlayer from "react-player";
import { Check, Volume2 } from "lucide-react";
import Link from "next/link";
import ControlsContainer from "components/molecules/ControlsContainer";
import { ImageDTO } from "types";
import * as Styled from "./GalleryImage.styled";
interface GalleryImageProps {
  item: ImageDTO;
  isUserImage: (id: string) => boolean;
  handleToggle: (id: string) => void;
  handleEditClick: (id?: string) => void;
  handleADetailerClick: (id?: string) => void;
  handleFaceSwapClick: (id?: string) => void;
  handleBGRemovalClick: (id?: string) => void;
  handleInpaintClick: (id?: string) => void;
  handleUpscaleClick: (id?: string) => void;
  handleAudioClick: (id?: string) => void;
  prepareForDelete: (id: string) => void;
  handleVote: (id: string) => void;
  onImageClick: (image: any) => void;
  isSelectionMode: boolean;
  handleSelectImage: (imageId: string) => void;
  isSelected: boolean;
  userGallery: boolean;
  handleShareClick: (e: React.MouseEvent, imageId?: string) => void;
  showIcons?: boolean;
  handleCompetitionsClick: () => void;
  showDeleteConfirmation: boolean;
  priority?: boolean; // Add this prop for above-the-fold images
}
const GalleryImage: React.FC<GalleryImageProps> = ({
  item,
  isUserImage,
  handleToggle,
  handleEditClick,
  handleADetailerClick,
  handleFaceSwapClick,
  handleBGRemovalClick,
  handleInpaintClick,
  handleUpscaleClick,
  handleAudioClick,
  prepareForDelete,
  handleVote,
  onImageClick,
  isSelectionMode,
  handleSelectImage,
  isSelected,
  userGallery,
  handleShareClick,
  showIcons = true,
  handleCompetitionsClick,
  priority = false // Add this prop for above-the-fold images
}) => {
  const handleMouseDown = (e: React.MouseEvent<HTMLElement>, itemId: string) => {
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      window.open(`/images/${itemId}`, "_blank", "noopener,noreferrer");
    }
  };
  const [retryCount, setRetryCount] = useState(0);
  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error(`Error loading image: ${e.currentTarget.src}`);
    if (retryCount < 5) {
      setRetryCount(prevCount => prevCount + 1);
      setTimeout(() => {
        // Append a timestamp to bypass potential caching issues
        const newSrc = `${e.currentTarget.src.split("?")[0]}?t=${Date.now()}`;
        e.currentTarget.src = newSrc;
      }, 10000);
    } else {
      console.error(`Failed to load image after ${retryCount} attempts:`, item.webpImage || item.image);
      e.currentTarget.style.display = "none";
    }
  };
  const renderGalleryImage = (image: ImageDTO) => {
    const isVideo = image.image.endsWith(".mp4");
    const isAudio = image.image.endsWith(".mp3");
    return <Styled.ImageWrapper data-sentry-element="unknown" data-sentry-component="renderGalleryImage" data-sentry-source-file="index.tsx">
                <Link href={`/images/${item.id}`} key={item.id} passHref data-sentry-element="Link" data-sentry-source-file="index.tsx">
                    <Styled.ImageContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {isVideo ? <ReactPlayer url={image.image} playing controls={priority} width="100%" height="100%" loop muted playsinline onClick={(e: {
            button: number;
            preventDefault: () => void;
          }) => {
            if (e.button === 0) {
              e.preventDefault();
            }
            isSelectionMode && userGallery ? handleSelectImage(item.id) : onImageClick(item);
          }} onAuxClick={(e: React.MouseEvent<HTMLElement, globalThis.MouseEvent>) => handleMouseDown(e, item.id)} /> : isAudio ? <div className="w-full h-full flex items-center justify-center" onClick={(e: {
            button: number;
            preventDefault: () => void;
          }) => {
            if (e.button === 0) {
              e.preventDefault();
            }
            isSelectionMode && userGallery ? handleSelectImage(item.id) : onImageClick(item);
          }} onAuxClick={(e: React.MouseEvent<HTMLElement, globalThis.MouseEvent>) => handleMouseDown(e, item.id)}>
                                <div className="flex flex-col items-center justify-center">
                                    <Volume2 className="w-14 h-14 text-gray-300" />
                                    <span className="text-lg text-gray-500">
                                        Audio (Click to Listen)
                                    </span>
                                </div>
                            </div> : <Styled.LazyLoadImage src={image.webpImage || image.image} alt={`Generated artwork ${image.id}`} loading={priority ? "eager" : "lazy"} width={800} height={800} onClick={e => {
            if (e.button === 0) {
              e.preventDefault();
            }
            isSelectionMode && userGallery ? handleSelectImage(item.id) : onImageClick(item);
          }} onAuxClick={e => handleMouseDown(e, item.id)} onError={e => {
            handleError(e);
          }} />}
                    </Styled.ImageContainer>
                </Link>
                <Styled.SelectButtonWrapper className={`${isSelected || isSelectionMode ? "opacity-100" : "opacity-0"}`} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.SelectButton $isSelected={isSelected} onClick={e => {
          e.stopPropagation();
          handleSelectImage(item.id);
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Check className={`h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`} data-sentry-element="Check" data-sentry-source-file="index.tsx" />
                    </Styled.SelectButton>
                </Styled.SelectButtonWrapper>
            </Styled.ImageWrapper>;
  };
  return <Styled.GalleryItemWrapper $isSelected={isSelected} data-sentry-element="unknown" data-sentry-component="GalleryImage" data-sentry-source-file="index.tsx">
            {renderGalleryImage(item)}
            {showIcons && <ControlsContainer imageInfo={item} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscaleClick} handleAudioClick={handleAudioClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} />}
        </Styled.GalleryItemWrapper>;
};
export default GalleryImage;